/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import ServiceRegistry from "bundle/util/ServiceRegistry";
import Events, { FilePreviewEvents } from "bundle/constants/Events";
import { UIActionPositionTypes } from "bundle/constants/UIActionConstants";
import LegalDialogUtil from "app/util/LegalDialogUtil";
import IFrameMessageTypes from "common/constants/IFrameMessageTypes";
import { isIE, isLegacyEdge, dispatchUIActionClickEvent } from "app/util/AppUtil";
import AppConstants from "bundle/constants/AppConstants";
import AnalyticsConstants from "app/constants/AnalyticsConstants";

export default class EmbedModeService {
    preComponentMount = () => {
        this._handleIEDialogCase();
        this._handleHUDLegalDialog();
        this._handleAdobeSupportWarning();
    };

    _handleIEDialogCase = () => {
        if (isIE()) {
            let parentElement;
            setInterval(() => {
                if (ServiceRegistry.getService("AppStore").isSizedPreset
                && !ServiceRegistry.getService("AppStore").inFullScreen) {
                    // Do nothing
                } else if (document.querySelector(".react-spectrum-Dialog")) {
                    parentElement = document.querySelector(".react-spectrum-Dialog").parentElement;
                    if (parentElement.style.position === "relative") {
                        parentElement.style.position = "";
                    } else if (parentElement.parentElement.style.position === "relative") {
                        parentElement.parentElement.style.position = "";
                    }
                }
            }, 100);
        }
    };

    _handleHUDLegalDialog = () => {
        const appStore = ServiceRegistry.getService("AppStore");
        if (appStore.isInLinePreset || (appStore.enableModernViewer && appStore.isSizedPreset)) {
            const uiActionConfigStore = appStore.getUIActionConfig();
            const uiConfigs = uiActionConfigStore.getUIActionConfig(UIActionPositionTypes.HUD_ELLIPSES);
            const hudIcons = [];
            const addUIControlInHud = iconDataConfig => {
                ServiceRegistry.getService("EventsService").registerEventListener(
                    Events.APP_RENDERING_DONE,
                    () => {
                        appStore.previewRef.getPublicApis()
                            .then(api => {
                                iconDataConfig.forEach(iconData => {
                                    api.hud.addUIControl(iconData);
                                });
                            });
                    },
                );
            };
            if (appStore.configStore.getConfig().showLegalNotice) {
                const showLegalDialog = () => {
                    ServiceRegistry.getService("DialogApi").showLegalDialog();
                };
                if (!appStore.legalDialogIdInHUD) {
                    appStore.legalDialogIdInHUD = true;
                    hudIcons.push(LegalDialogUtil.getLegalDialogData(showLegalDialog));
                }
            }
            // handling for custom HUD_ELLIPSES icons
            uiConfigs.forEach(iconConfig => {
                const uiConfigClickHandler = () => {
                    dispatchUIActionClickEvent(iconConfig);
                };
                const iconData = {
                    title: iconConfig.label,
                    clickHandler: uiConfigClickHandler,
                };
                hudIcons.push(iconData);
            });

            addUIControlInHud(hudIcons);
        }
    };

    _handleAdobeSupportWarning = () => {
        const appStore = ServiceRegistry.getService("AppStore");
        ServiceRegistry.getService("EventsService").registerEventListener(
            Events.APP_RENDERING_DONE,
            () => {
                if (isIE() || isLegacyEdge()) {
                    appStore.setToastInfo({
                        type: AppConstants.SHOW_TOAST.ERROR,
                        messageId: `${ isIE() ? "IE11.support.warning" : "Legacy.Edge.support.warning" }`,
                    });
                    ServiceRegistry.getService("AnalyticsProviderService")
                        .sendEvent(AnalyticsConstants.TOAST_SHOWN);
                    const toastElement = document.querySelector(".sdk-Toast-toast");
                    if (toastElement && appStore.isInLinePreset) {
                        toastElement.style.bottom = "auto";
                    }
                }
            },
        );
    }

    FWOpenPDFViewer = () => {
        ServiceRegistry.getService("EventsService").registerEventListener(
            Events.VIEWER_UNMOUNT,
            this.FWExitPDFViewer,
        );
    };

    FWExitPDFViewer = () => {
        this._pdfViewerClose();
    };

    LBOpenPDFViewer = isIOS => {
        ServiceRegistry.getService("EventsService").registerEventListener(
            FilePreviewEvents.PREVIEW_DOCUMENT_CLICK,
            this.LBExitPDFViewer,
        );
        document.addEventListener("keydown", this._handleLBPDFViewerEscape);
        ServiceRegistry.getService("MessagingService").sendMessage(IFrameMessageTypes.LB_OPEN_PDF_VIEWER, { isIOS });
    };

    LBExitPDFViewer = () => {
        this._pdfViewerClose();
        document.removeEventListener("keydown", this._handleLBPDFViewerEscape);
        ServiceRegistry.getService("MessagingService").sendMessage(IFrameMessageTypes.LB_EXIT_PDF_VIEWER);
    };

    pdfViewerOpen = () => {
        ServiceRegistry.getService("EventsService").sendEvent(Events.PDF_VIEWER_OPEN);
    };

    _pdfViewerClose = () => {
        ServiceRegistry.getService("EventsService").sendEvent(Events.PDF_VIEWER_CLOSE);
    };

    _handleLBPDFViewerEscape = event => {
        if ((event.key === "Escape" || event.key === "Esc")) {
            const appStore = ServiceRegistry.getService("AppStore");
            if (!appStore.legalDialogOpen && !appStore.searchOpen) {
                this.LBExitPDFViewer();
            }
        }
    };
}
